import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * Configuración de Axios para comunicación con el backend
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adjuntar el token (si existe) en cada petición
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lotea_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor global para mostrar toasts en errores de red/respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si el token expiró (401), limpiar la sesión
    if (error?.response?.status === 401) {
      localStorage.removeItem('lotea_token');
      localStorage.removeItem('lotea_user');
      delete api.defaults.headers.common['Authorization'];
      // Disparar evento custom para que los componentes se actualicen
      window.dispatchEvent(new Event('logout'));
      toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    } else {
      const mensaje = error?.response?.data?.error || error?.message || 'Error de red';
      toast.error(mensaje);
    }
    return Promise.reject(error);
  }
);

/**
 * Servicio de API para Lotes
 * Contiene todas las funciones para interactuar con el backend
 */
export const loteService = {
  /**
   * Obtiene todos los lotes con filtros opcionales
   * @param {Object} filters - Filtros de búsqueda
   * @returns {Promise<Array>} Lista de lotes
   */
  obtenerLotes: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.areaMin) params.append('areaMin', filters.areaMin);
    if (filters.areaMax) params.append('areaMax', filters.areaMax);
    if (filters.precioMin) params.append('precioMin', filters.precioMin);
    if (filters.precioMax) params.append('precioMax', filters.precioMax);
    if (filters.plano) params.append('plano', filters.plano);

    const response = await api.get(`/lotes?${params.toString()}`);
    return response.data.data;
  },

  obtenerSiguienteNumero: async () => {
    const response = await api.get('/lotes/next-number');
    return response.data.data;
  },

  /**
   * Obtiene un lote por su ID
   * @param {String} id - ID del lote
   * @returns {Promise<Object>} Lote
   */
  obtenerLotePorId: async (id) => {
    const response = await api.get(`/lotes/${id}`);
    return response.data.data;
  },

  /**
   * Crea un nuevo lote
   * @param {Object} lote - Datos del lote
   * @returns {Promise<Object>} Lote creado
   */
  crearLote: async (lote) => {
    const response = await api.post('/lotes', lote);
    return response.data.data;
  },

  /**
   * Actualiza un lote existente
   * @param {String} id - ID del lote
   * @param {Object} lote - Datos actualizados
   * @returns {Promise<Object>} Lote actualizado
   */
  actualizarLote: async (id, lote) => {
    const response = await api.put(`/lotes/${id}`, lote);
    return response.data.data;
  },

  /**
   * Elimina un lote
   * @param {String} id - ID del lote
   * @returns {Promise<void>}
   */
  eliminarLote: async (id) => {
    await api.delete(`/lotes/${id}`);
  },

  /**
   * Obtiene estadísticas de los lotes
   * @returns {Promise<Object>} Estadísticas
   */
  obtenerEstadisticas: async () => {
    const response = await api.get('/lotes/estadisticas');
    return response.data.data;
  },
};

export default api;
